/**
 * BlockExplorer.js
 * ================
 * Main Block Explorer component for Antelope blockchain
 * 
 * Features:
 *   - Transaction list with advanced filtering
 *   - Real-time RAM price display
 *   - Blockchain node heartbeat status
 *   - Transaction details dialog
 *   - Modern, eye-catching UI
 * 
 * Data Source: Memento API (preferred approach)
 * 
 * Author: Block Explorer Team
 * Date: January 2026
 */

import React, { useState, useEffect, useRef } from 'react';
import { Card } from 'primereact/card';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Tag } from 'primereact/tag';
import { Tooltip } from 'primereact/tooltip';
import { ProgressSpinner } from 'primereact/progressspinner';
import { FilterMatchMode } from 'primereact/api';
import { ContractKit } from "@wharfkit/contract";
import { APIClient } from '@wharfkit/antelope';
import moment from 'moment';

import TransactionDetails from './TransactionDetails';
import AccountDetails from './AccountDetails';
import NodeHeartbeat from './NodeHeartbeat';
import CleosInfo from './CleosInfo';
import config from './blockexplorer-config.json';
import './BlockExplorer.css';

const BlockExplorer = () => {
    // State management
    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [ramPrice, setRamPrice] = useState(0);
    const [selectedTx, setSelectedTx] = useState(null);
    const [txDialogVisible, setTxDialogVisible] = useState(false);
    const [globalFilterValue, setGlobalFilterValue] = useState('');
    const [lastUpdate, setLastUpdate] = useState(null);
    const [totalTxCount, setTotalTxCount] = useState(0);
    
    // Account lookup state
    const [accountName, setAccountName] = useState('');
    const [selectedAccount, setSelectedAccount] = useState(null);
    const [accountDialogVisible, setAccountDialogVisible] = useState(false);
    
    // Advanced filters
    const [filters, setFilters] = useState({
        global: { value: null, matchMode: FilterMatchMode.CONTAINS },
        account: { value: '', matchMode: FilterMatchMode.CONTAINS },
        action: { value: '', matchMode: FilterMatchMode.CONTAINS },
        actor: { value: '', matchMode: FilterMatchMode.CONTAINS }
    });

    const dt = useRef(null);
    const refreshInterval = useRef(null);
    const abortController = useRef(null);

    // Initialize on component mount
    useEffect(() => {
        initializeExplorer();
        
        // Set up auto-refresh every 30 seconds
        refreshInterval.current = setInterval(() => {
            loadTransactions(false);
            updateRamPrice();
        }, 30000);

        return () => {
            if (refreshInterval.current) {
                clearInterval(refreshInterval.current);
            }
            // Cancel any pending requests
            if (abortController.current) {
                abortController.current.abort();
            }
        };
    }, []);

    // Initialize explorer
    const initializeExplorer = async () => {
        setLoading(true);
        await Promise.all([
            loadTransactions(true),
            updateRamPrice()
        ]);
        setLoading(false);
    };

    // Load transactions from backend API (MariaDB)
    const loadTransactions = async (showLoader = true) => {
        // Cancel any pending request
        if (abortController.current) {
            abortController.current.abort();
        }
        
        // Create new abort controller
        abortController.current = new AbortController();
        
        if (showLoader) setLoading(true);
        
        try {
            const limit = 100;
            const response = await fetch(
                `${config.fallbackEndpoint}/testsql?limit=${limit}`,
                { signal: abortController.current.signal }
            );
            
            if (!response.ok) {
                throw new Error(`Failed to fetch transactions (${response.status})`);
            }

            const data = await response.json();
            
            // Handle error responses
            if (data.error) {
                throw new Error(data.message || 'Backend error');
            }
            
            // Transform backend API data
            const transformedData = data.map(tx => {
                const trace = typeof tx.trace === 'string' ? JSON.parse(tx.trace) : tx.trace;
                return {
                    trx_id: tx.trx_id || 'N/A',
                    block_num: tx.block_num || trace.block_num || 0,
                    block_timestamp: tx.block_time || trace.block_timestamp,
                    account: trace.trace?.action_traces?.[0]?.act?.account || 'N/A',
                    action: trace.trace?.action_traces?.[0]?.act?.name || 'N/A',
                    actor: trace.trace?.action_traces?.[0]?.receipt?.auth_sequence?.[0]?.account || 
                           trace.trace?.action_traces?.[0]?.act?.authorization?.[0]?.actor || 'N/A',
                    status: trace.trace?.status || 'executed',
                    cpu_usage: trace.trace?.cpu_usage_us || 0,
                    net_usage: trace.trace?.net_usage || 0,
                    irreversible: tx.irreversible || false,
                    rawData: tx
                };
            });

            setTransactions(transformedData);
            setTotalTxCount(transformedData.length);
            setLastUpdate(new Date());
            
        } catch (error) {
            // Ignore abort errors
            if (error.name === 'AbortError') {
                console.log('Request was cancelled');
                return;
            }
            console.error('Error loading transactions:', error);
            // Set empty state if loading fails
            setTransactions([]);
            setTotalTxCount(0);
        } finally {
            if (showLoader) setLoading(false);
        }
    };

    // Update RAM price
    const updateRamPrice = async () => {
        try {
            const contractKit = new ContractKit({
                client: new APIClient({ url: config.blockchainEndpoint })
            });

            const contract = await contractKit.load("eosio");
            const cursor = contract.table("rammarket").query();
            const ramMarket = await cursor.all();
            
            if (ramMarket && ramMarket.length > 0) {
                const baseBalance = Number(ramMarket[0].base.balance.toString().split(" ")[0]);
                const quoteBalance = Number(ramMarket[0].quote.balance.toString().split(" ")[0]);
                const price = (quoteBalance / baseBalance) * 1024;
                setRamPrice(price);
            }
        } catch (error) {
            console.error('Error fetching RAM price:', error);
        }
    };

    // View transaction details
    const viewTransactionDetails = async (rowData) => {
        setSelectedTx(rowData);
        setTxDialogVisible(true);
    };

    // Template functions for DataTable columns
    const blockNumTemplate = (rowData) => {
        return (
            <span className="block-number">
                #{rowData.block_num.toLocaleString()}
            </span>
        );
    };

    const timestampTemplate = (rowData) => {
        if (!rowData.block_timestamp) return <span>N/A</span>;
        
        // Block times are UTC (with or without a 'Z'). Convert to the viewer's local time zone,
        // which also follows summer time; same as formatDateTime in TransactionDetails.
        const timestampStr = typeof rowData.block_timestamp === 'string'
            ? rowData.block_timestamp.replace(' ', 'T')
            : rowData.block_timestamp;
        const utcTime = moment.utc(timestampStr);
        const displayTime = utcTime.clone().local();

        return (
            <div className="timestamp-cell">
                <i className="pi pi-clock" style={{ marginRight: '0.5rem', color: '#94a3b8' }}></i>
                <span title={`UTC: ${utcTime.format('YYYY-MM-DD HH:mm:ss')} | Local: ${displayTime.format('YYYY-MM-DD HH:mm:ss Z')}`}>
                    {displayTime.format('MMM DD, HH:mm:ss')}
                </span>
            </div>
        );
    };

    const accountTemplate = (rowData) => {
        return <code className="code-highlight">{rowData.account}</code>;
    };

    const actionTemplate = (rowData) => {
        return (
            <Tag 
                value={rowData.action} 
                severity="info"
                className="action-tag"
            />
        );
    };

    const actorTemplate = (rowData) => {
        return <code className="code-highlight">{rowData.actor}</code>;
    };

    const statusTemplate = (rowData) => {
        const severityMap = {
            'executed': 'success',
            'failed': 'danger',
            'soft_fail': 'warning',
            'hard_fail': 'danger',
            'unknown': 'secondary'
        };

        return (
            <Tag 
                value={rowData.status} 
                severity={severityMap[rowData.status] || 'secondary'}
                className="status-tag"
            />
        );
    };

    const txIdTemplate = (rowData) => {
        return (
            <div className="tx-id-cell">
                <code className="tx-id-short">{rowData.trx_id.substring(0, 12)}...</code>
                <Button
                    icon="pi pi-external-link"
                    className="p-button-rounded p-button-text p-button-sm"
                    onClick={() => viewTransactionDetails(rowData)}
                    tooltip="View details"
                    tooltipOptions={{ position: 'top' }}
                />
            </div>
        );
    };

    const resourceTemplate = (rowData) => {
        return (
            <div className="resource-cell">
                <span className="resource-item">
                    <i className="pi pi-bolt" style={{ fontSize: '0.8rem' }}></i>
                    {rowData.cpu_usage} μs
                </span>
                <span className="resource-item">
                    <i className="pi pi-database" style={{ fontSize: '0.8rem' }}></i>
                    {rowData.net_usage} B
                </span>
            </div>
        );
    };

    // Global filter
    const onGlobalFilterChange = (e) => {
        const value = e.target.value;
        let _filters = { ...filters };
        _filters['global'].value = value;
        setFilters(_filters);
        setGlobalFilterValue(value);
    };

    // Custom filter change
    const onCustomFilterChange = (field, value) => {
        let _filters = { ...filters };
        _filters[field].value = value;
        setFilters(_filters);
    };

    // Clear all filters
    const clearFilters = () => {
        setFilters({
            global: { value: null, matchMode: FilterMatchMode.CONTAINS },
            account: { value: '', matchMode: FilterMatchMode.CONTAINS },
            action: { value: '', matchMode: FilterMatchMode.CONTAINS },
            actor: { value: '', matchMode: FilterMatchMode.CONTAINS }
        });
        setGlobalFilterValue('');
    };

    // Export transactions
    const exportTransactions = () => {
        dt.current.exportCSV();
    };

    // Handle account lookup
    const handleAccountLookup = () => {
        if (accountName && accountName.trim()) {
            setSelectedAccount(accountName.trim());
            setAccountDialogVisible(true);
        }
    };

    // Handle Enter key press in account input
    const handleAccountKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleAccountLookup();
        }
    };

    // Header component
    const renderHeader = () => {
        return (
            <div className="table-header">
                <div className="header-left">
                    <h3 className="header-title">
                        <i className="pi pi-list" style={{ marginRight: '0.5rem' }}></i>
                        Recent Transactions
                    </h3>
                    <span className="transaction-count">
                        {totalTxCount} transactions
                    </span>
                </div>
                <div className="header-right">
                    <span className="p-input-icon-left search-input">
                        <i className="pi pi-search" />
                        <InputText
                            value={globalFilterValue}
                            onChange={onGlobalFilterChange}
                            placeholder="Search transactions..."
                            autoComplete="off"
                            autoCorrect="off"
                            autoCapitalize="off"
                            spellCheck="false"
                        />
                    </span>
                    <span className="p-input-icon-left account-lookup-inline">
                        <i className="pi pi-user" />
                        <InputText
                            value={accountName}
                            onChange={(e) => setAccountName(e.target.value)}
                            onKeyPress={handleAccountKeyPress}
                            placeholder="Account name..."
                            maxLength={12}
                            className="account-input-inline"
                            autoComplete="off"
                            autoCorrect="off"
                            autoCapitalize="off"
                            spellCheck="false"
                        />
                    </span>
                    <Button
                        icon="pi pi-search"
                        tooltip="Lookup Account"
                        tooltipOptions={{ position: 'top' }}
                        onClick={handleAccountLookup}
                        disabled={!accountName || !accountName.trim()}
                        className="p-button-info account-lookup-btn"
                    />
                    <Button
                        icon="pi pi-filter-slash"
                        label="Clear"
                        className="p-button-outlined"
                        onClick={clearFilters}
                    />
                    <Button
                        icon="pi pi-refresh"
                        label="Refresh"
                        onClick={() => loadTransactions(true)}
                        loading={loading}
                    />
                    <Button
                        icon="pi pi-download"
                        label="Export"
                        className="p-button-success"
                        onClick={exportTransactions}
                    />
                </div>
            </div>
        );
    };

    // Custom filter inputs for specific columns
    const accountFilterElement = (
        <InputText
            value={filters.account.value}
            onChange={(e) => onCustomFilterChange('account', e.target.value)}
            placeholder="Filter by account"
            className="p-column-filter"
            style={{ width: '100%' }}
        />
    );

    const actionFilterElement = (
        <InputText
            value={filters.action.value}
            onChange={(e) => onCustomFilterChange('action', e.target.value)}
            placeholder="Filter by action"
            className="p-column-filter"
            style={{ width: '100%' }}
        />
    );

    const actorFilterElement = (
        <InputText
            value={filters.actor.value}
            onChange={(e) => onCustomFilterChange('actor', e.target.value)}
            placeholder="Filter by actor"
            className="p-column-filter"
            style={{ width: '100%' }}
        />
    );

    return (
        <div className="block-explorer">
            {/* Header Section */}
            <div className="explorer-header">
                <div className="header-content">
                    <div className="logo-section">
                        <i className="pi pi-box" style={{ fontSize: '2.5rem', color: '#6366f1' }}></i>
                        <div>
                            <h1 className="explorer-title">Block Explorer</h1>
                            <p className="explorer-subtitle">Antelope Blockchain Monitor</p>
                        </div>
                    </div>
                    
                    {/* Stats Cards */}
                    <div className="stats-cards">
                        {/* RAM Price Card */}
                        <Card className="stat-card ram-card">
                            <div className="stat-content">
                                <i className="pi pi-database stat-icon"></i>
                                <div className="stat-details">
                                    <span className="stat-label">RAM Price</span>
                                    <span className="stat-value">
                                        {ramPrice ? (Math.floor(ramPrice * 1e8) / 1e8).toFixed(8) : '---'}
                                    </span>
                                    <span className="stat-unit">SYS/KB</span>
                                </div>
                            </div>
                        </Card>

                        {/* Last Update Card */}
                        <Card className="stat-card update-card">
                            <div className="stat-content">
                                <i className="pi pi-clock stat-icon"></i>
                                <div className="stat-details">
                                    <span className="stat-label">Last Update</span>
                                    <span className="stat-value">
                                        {lastUpdate ? moment(lastUpdate).format('HH:mm:ss') : '---'}
                                    </span>
                                    <span className="stat-unit">
                                        {lastUpdate ? moment(lastUpdate).fromNow() : ''}
                                    </span>
                                </div>
                            </div>
                        </Card>

                        {/* Transactions Card */}
                        <Card className="stat-card tx-card">
                            <div className="stat-content">
                                <i className="pi pi-list stat-icon"></i>
                                <div className="stat-details">
                                    <span className="stat-label">Transactions</span>
                                    <span className="stat-value">{totalTxCount}</span>
                                    <span className="stat-unit">recent</span>
                                </div>
                            </div>
                        </Card>
                    </div>
                </div>
            </div>

            {/* Node Heartbeat Section */}
            <NodeHeartbeat endpoint={config.blockchainEndpoint} />

            {/* Main Transaction Table */}
            <Card className="transactions-card">
                {loading && transactions.length === 0 ? (
                    <div className="loading-container">
                        <ProgressSpinner />
                        <p>Loading transactions...</p>
                    </div>
                ) : (
                    <DataTable
                        ref={dt}
                        value={transactions}
                        paginator
                        rows={25}
                        rowsPerPageOptions={[10, 25, 50, 100]}
                        dataKey="trx_id"
                        filters={filters}
                        filterDisplay="row"
                        globalFilterFields={['trx_id', 'account', 'action', 'actor', 'status']}
                        header={renderHeader()}
                        emptyMessage="No transactions found"
                        className="transactions-table"
                        stripedRows
                        responsiveLayout="scroll"
                        sortField="block_num"
                        sortOrder={-1}
                        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                        currentPageReportTemplate="Showing {first} to {last} of {totalRecords} transactions"
                    >
                        <Column
                            field="block_num"
                            header="Block"
                            body={blockNumTemplate}
                            sortable
                            style={{ minWidth: '100px' }}
                        />
                        <Column
                            field="block_timestamp"
                            header="Time"
                            body={timestampTemplate}
                            sortable
                            style={{ minWidth: '180px' }}
                        />
                        <Column
                            field="account"
                            header="Contract"
                            body={accountTemplate}
                            filter
                            filterElement={accountFilterElement}
                            showFilterMenu={false}
                            style={{ minWidth: '150px' }}
                        />
                        <Column
                            field="action"
                            header="Action"
                            body={actionTemplate}
                            filter
                            filterElement={actionFilterElement}
                            showFilterMenu={false}
                            style={{ minWidth: '130px' }}
                        />
                        <Column
                            field="actor"
                            header="Actor"
                            body={actorTemplate}
                            filter
                            filterElement={actorFilterElement}
                            showFilterMenu={false}
                            style={{ minWidth: '150px' }}
                        />
                        <Column
                            field="status"
                            header="Status"
                            body={statusTemplate}
                            style={{ minWidth: '120px' }}
                        />
                        <Column
                            header="Resources"
                            body={resourceTemplate}
                            style={{ minWidth: '150px' }}
                        />
                        <Column
                            field="trx_id"
                            header="Transaction ID"
                            body={txIdTemplate}
                            style={{ minWidth: '180px' }}
                        />
                    </DataTable>
                )}
            </Card>

            {/* Transaction Details Dialog */}
            <TransactionDetails
                visible={txDialogVisible}
                transaction={selectedTx}
                onHide={() => setTxDialogVisible(false)}
                mementoAPI={config.mementoAPI}
            />

            {/* Account Details Dialog */}
            <AccountDetails
                visible={accountDialogVisible}
                accountName={selectedAccount}
                onHide={() => setAccountDialogVisible(false)}
                blockchainEndpoint={config.blockchainEndpoint}
            />

            {/* Live cleos node-info widget (updates every 10 s) */}
            <CleosInfo />
        </div>
    );
};

export default BlockExplorer;
